/* eslint-disable */
import { Badge, Button } from "@mui/material";
import {
  DateCalendar,
  DayCalendarSkeleton,
  LocalizationProvider,
  PickersDay,
  PickersDayProps,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import React, { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { FormProvider, SubmitHandler, useFieldArray, useForm, useWatch, Control } from "react-hook-form";
import { timeZoneList } from "../../utils";
import useYupValidationResolver from "src/hooks/useYupValidationResolver";
import * as yup from "yup";
import RHFSelect from "src/components/elements/Select/RHFSelect";
import AddNewSlots from "../AddNewSlots";
import {
  AddSlotsProps,
  CreateInterviewSlotsRequest,
  HighlightedDay,
  HighlightedDaysRequest,
  InterviewPanelProps,
  MonthSlotProps,
  SlotsProps,
} from "../../types";
import { RiDeleteBinLine } from "react-icons/ri";
import { useRoleUser } from "src/hooks/query/usersByRole";
import { useAuth } from "@panna/auth";
import RHFAutoComplete from "src/components/elements/AutoComplete/RHFAutoComplete";
import { confirmWrapper } from "src/components/Dialog";
import RHFInput from "src/components/elements/Input/RHFInput";
import { AiOutlineMinus, AiOutlinePlus } from "react-icons/ai";
import theme from "src/theme";

interface CreateSlotsProps {
  highlightedDays: Array<HighlightedDay>;
  isCalendarLoading: boolean;
  monthSlots: MonthSlotProps;
  isATSRecruiter: boolean;
  isATSInterviewer: boolean;
  getSlotsByMonth: (data: HighlightedDaysRequest) => Promise<void>;
  saveInterviewSlots: (data: CreateInterviewSlotsRequest) => Promise<void>;
  deleteInterviewSlot: (slotId: number) => Promise<void>;
}

interface CreateNewSlotsProps {
  timeZone: string;
  interviewers?: number;
  interviewPanel?: Array<InterviewPanelProps>;
}

// Helper functions moved outside the component
const timeToMinutes = (time: string | null): number => {
  if (!time) return 0;
  const [hour, minute] = moment(time, "hh:mm A")
    .format("HH:mm")
    .split(":")
    .map(Number);
  return hour * 60 + minute;
};

const hasOverlap = (slot1: AddSlotsProps, slot2: SlotsProps, timeZone: string): boolean => {
  // Allow overlapping if time zones are different
  if (timeZone !== slot2.timeZone) return false;

  const start1 = timeToMinutes(slot1.startTime);
  const end1 = timeToMinutes(slot1.endTime);
  const start2 = timeToMinutes(slot2.startTime);
  const end2 = timeToMinutes(slot2.endTime);
  return start1 < end2 && start2 < end1;
};

const isDuplicate = (slot1: AddSlotsProps, slot2: SlotsProps, timeZone: string): boolean => {
  return (
    slot1.startTime === slot2.startTime &&
    slot1.endTime === slot2.endTime &&
    timeZone === slot2.timeZone
  );
};

// Custom Day render component extracted
function ServerDay(props: PickersDayProps<Dayjs> & { highlightedDays: Array<HighlightedDay> }) {
  const { day, outsideCurrentMonth, highlightedDays, ...other } = props;
  const dayString = day.format("MM/DD/YYYY");
  const highlightedDay = highlightedDays.find(
    (highlight) => highlight.date === dayString
  );
  const isSelected = !outsideCurrentMonth && highlightedDay !== undefined;

  return (
    <Badge
      key={day.toString()}
      overlap="circular"
      badgeContent={
        isSelected ? (
          <div className="w-[10px] h-[10px] flex justify-center rounded-xl bg-lightGray border-[1px] border-solid border-primary absolute top-1 left-0 text-primary font-extrabold text-[8px]">
            {highlightedDay?.slots}
          </div>
        ) : undefined
      }
    >
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
      />
    </Badge>
  );
}

// Define props interface for InterviewPanelMembers
interface InterviewPanelMembersProps {
  control: Control<any>;
  fields?: Array<InterviewPanelProps>;
  remove: (index: number) => void;
}

// Panel Member component
const InterviewPanelMembers = ({ control, fields, remove }: InterviewPanelMembersProps) => {
  const deletePanelMember = async (index: number) => {
    remove(index);
  };

  return (
    <>
      {fields?.map((member: InterviewPanelProps, index: number) => (
        <div key={member.interviewPanelId} className="flex gap-3 mb-2 items-start relative">
          <div className="w-full">
            <RHFInput
              name={`interviewPanel[${index}].name`}
              placeholder="Name"
              control={control}
            />
          </div>
          <div className="w-full">
            <RHFInput
              name={`interviewPanel[${index}].email`}
              placeholder="Email"
              control={control}
            />
          </div>
          <AiOutlineMinus
            color="white"
            className="bg-primary rounded-lg absolute top-3 -right-7 cursor-pointer"
            onClick={() => deletePanelMember(index)}
          />
        </div>
      ))}
    </>
  );
};

// Define props interface for ExistingSlots
interface ExistingSlotsProps {
  daySlots?: SlotsProps[];
  handleDeleteSlot: (slotId: number) => Promise<void>;
}

// Existing slots display component
const ExistingSlots = ({ daySlots, handleDeleteSlot }: ExistingSlotsProps) => {
  if (!daySlots?.length) return null;
  
  return (
    <div className="mt-1">
      <div className="text-[#4D586A]">
        <div className="font-semibold">Open Slots:</div>
        <div className="max-h-[400px] w-fit overflow-y-auto mt-2 flex flex-col gap-2">
          {daySlots.map((slot: SlotsProps, index: number) => (
            <div
              key={index}
              className="w-fit group hover:bg-[#4d586a0f] p-1 rounded-md"
            >
              <div className="flex items-center gap-2 cursor-default w-fit">
                <div className="flex gap-2">
                  <div>{slot.startTime}</div>
                  <div>-</div>
                  <div>{slot.endTime}</div>
                  <div>{slot.timeZone}</div>
                </div>
                <div
                  onClick={() => handleDeleteSlot(slot.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xl text-secondary hover:text-primary cursor-pointer"
                >
                  <RiDeleteBinLine />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CreateSlots = function CreateSlots(props: CreateSlotsProps) {
  const {
    highlightedDays,
    isCalendarLoading,
    monthSlots,
    isATSRecruiter,
    isATSInterviewer,
    getSlotsByMonth,
    saveInterviewSlots,
    deleteInterviewSlot,
  } = props;

  // State management
  const [slotsList, setSlotsList] = useState<AddSlotsProps[]>([]);
  const [date, setDate] = useState<number | undefined>(new Date().valueOf());
  const [daySlots, setDaySlots] = useState<SlotsProps[]>();

  // Auth and user data
  const authContext = useAuth();
  const { authState: auth } = authContext;
  const userId = auth?.userInfo?.id;
  const { userByRoleOptionsWithEmail, usersLoading, fetchByRole } = useRoleUser({
    roleNameList: "ATSInterviewer",
  });

  // Form validation
  const validationSchema = yup.object().shape({
    timeZone: yup
      .string()
      .typeError("Select time zone")
      .required("Time zone required"),
    interviewers: yup.number()
      .typeError("Select interviewer")
      .required("Interviewer required"),
    interviewPanel: yup.array().of(
      yup.object().shape({
        name: yup.string().required("Name is required"),
        email: yup.string().email("Invalid email format").required("Email is required"),
      })
    ),
  });
  
  const resolver = useYupValidationResolver(validationSchema);

  // Form setup
  const methods = useForm<CreateNewSlotsProps>({
    defaultValues: {
      timeZone: "IST",
      interviewers: (isATSInterviewer && !isATSRecruiter) ? userId as number : undefined,
    },
    resolver,
  });

  const { handleSubmit, watch, control, setValue } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'interviewPanel',
    keyName:'interviewPanelId'
  });
  const interviewPanel = useWatch({ name: 'interviewPanel', control });

  // Calendar and date management handlers
  const handleMonthOrYearChange = (date: Dayjs) => {
    setDate(date.valueOf());
    setDaySlots(undefined);
    const interviewerId = watch("interviewers");
    const data: HighlightedDaysRequest = {
      month: date.month(),
      year: date.year(),
      interviewerId: interviewerId ? interviewerId : userId,
    };
    getSlotsByMonth(data);
  };

  const handleDateChange = (date: number | undefined) => {
    if (date !== undefined) {
      const formattedDate: string = moment(date).format("MM/DD/YYYY");
      const daySlots = monthSlots?.[formattedDate];
      setDaySlots(daySlots);
    }
  };

  // Slot management handlers
  const handleAddNewSlotToList = (slots: AddSlotsProps[]) => {
    setSlotsList(slots);
  };

  const removeSlotFromSlotList = (id: string | undefined) => {
    setSlotsList((prevSlots) => prevSlots.filter((item) => item.id !== id));
  };

  const handleDeleteSlot = async (slotId: number) => {
    await deleteInterviewSlot(slotId);
    handleMonthOrYearChange(dayjs(date));
  };

  // Form submission handlers
  const submitHandler: SubmitHandler<CreateNewSlotsProps> = async (data) => {
    try {
      handleCreateNewSlots();
    } catch (e) {
      console.info("...");
    }
  };

  const handleCreateNewSlots = async () => {
    const timeZone = watch("timeZone");
    const interviewerId = watch("interviewers");
    const interviewPanel = watch("interviewPanel");

    // Check for duplicates and overlaps
    const existingSlots = daySlots || monthSlots?.[moment(date).format("MM/DD/YYYY")] || [];
    
    for (const newSlot of slotsList) {
      for (const existingSlot of existingSlots) {
        if (isDuplicate(newSlot, existingSlot, timeZone)) {
          confirmWrapper(
            `Duplicate slot detected: ${newSlot.startTime} - ${newSlot.endTime} ${timeZone}`,
            { hideSecondaryBtn: true, variant: "danger" }
          );
          return;
        }
        
        if (hasOverlap(newSlot, existingSlot, timeZone)) {
          confirmWrapper(
            `Overlapping slot detected: ${newSlot.startTime} - ${newSlot.endTime} ${timeZone}`,
            { hideSecondaryBtn: true, variant: "danger" }
          );
          return;
        }
      }
    }

    const finalData: CreateInterviewSlotsRequest = {
      timeSlots: slotsList,
      date: moment(date).format("MM/DD/YYYY"),
      timeZone,
      interviewerId: interviewerId ? interviewerId : (userId as number),
      interviewPanel,
    };
    
    await saveInterviewSlots(finalData);
    setValue("interviewPanel", []);
    setSlotsList([]);
    handleMonthOrYearChange(dayjs(date));
  };

  // Panel member handlers
  const handleAddPanelMember = () => {
    append({ name: '', email: '' });
  };

  // Side effects
  useEffect(() => {
    handleMonthOrYearChange(dayjs(date));
  }, [watch("interviewers")]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <div className="grid grid-cols-2 gap-5 p-10 text-left">
            <div className="col-span-2">
              <strong>Create Time Slot: </strong>(Select date to create multiple
              slots in one shot)
            </div>
            
            {/* Form Inputs Section */}
            <div className="col-span-2 flex gap-5">
              <div className="w-full">
                {isATSRecruiter && (
                  <div className="w-full">
                    <div className="font-bold text-md text-[#28364C] tracking-wide mb-1">
                      Select interviewer
                    </div>
                    <RHFAutoComplete
                      name="interviewers"
                      control={control}
                      options={userByRoleOptionsWithEmail}
                      loading={usersLoading}
                      placeholder="Select interviewer"
                    />
                  </div>
                )}
              </div>
              <div className="w-full">
                <RHFSelect
                  control={control}
                  name="timeZone"
                  label="Time zone"
                  options={timeZoneList}
                />
              </div>
            </div>
            
            {/* Interview Panel Section */}
            <div className="col-span-2">
              {fields?.length !== 0 && (
                <div className="font-bold text-md text-[#28364C] tracking-wide mb-1">
                  Interview Panel
                </div>
              )}
              <InterviewPanelMembers 
                control={control} 
                fields={fields} 
                remove={remove} 
              />
              <Button onClick={handleAddPanelMember} className="mt-2">
                <AiOutlinePlus color={theme.palette.primary.main} /> Add New Panel Member
              </Button>
            </div>
            
            {/* Calendar and Slots Section */}
            <div className="col-span-2 flex gap-5">
              <div className="w-full">
                <DateCalendar
                  defaultValue={dayjs(new Date())}
                  minDate={dayjs(new Date())}
                  loading={isCalendarLoading}
                  onMonthChange={handleMonthOrYearChange}
                  onYearChange={handleMonthOrYearChange}
                  renderLoading={() => <DayCalendarSkeleton />}
                  onChange={(e) => {
                    const dateValue = e?.valueOf();
                    setDate(dateValue);
                    handleDateChange(dateValue);
                  }}
                  slots={{
                    day: (props) => <ServerDay {...props} highlightedDays={highlightedDays} />,
                  }}
                  className="shadow-md bg-white"
                />
                <div>
                  {slotsList.length > 0 && (
                    <div className="text-sm text-primary mb-2">
                      You have entered {slotsList.length} new slot
                      {slotsList.length > 1 ? "s" : ""}
                    </div>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className="w-[150px] mt-5"
                    disabled={slotsList.length === 0}
                  >
                    Create Slot
                  </Button>
                </div>
              </div>
              
              {/* Slots Management Section */}
              <div className="w-full">
                <AddNewSlots
                  handleAddNewSlotToList={handleAddNewSlotToList}
                  removeSlotFromSlotList={removeSlotFromSlotList}
                  slotsList={slotsList}
                />
                <ExistingSlots daySlots={daySlots} handleDeleteSlot={handleDeleteSlot} />
              </div>
            </div>
          </div>
        </LocalizationProvider>
      </form>
    </FormProvider>
  );
};

CreateSlots.defaultProps = {};

CreateSlots.propTypes = {};

export default CreateSlots;
